import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Flex, Select, Table, Typography, message, theme, type TableColumnsType } from "antd";
import { useGetIdentity } from "@refinedev/core";
import {
  ChannelPermissionLevel,
  conversationsApi,
} from "@/services/conversations.api";
import {
  StoreOperator,
  StoreSeller,
  storePopulationsApi,
} from "@/services/store-populations.api";
import { NIVEL, NIVEL_PARA_TODO } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;

/**
 * Niveles de acceso al canal.
 *
 * El "para todo" no es decorativo: estos niveles son **channel-wide** (001, rev 4.3). Quien tiene
 * Viewer ve TODAS las conversaciones del canal, no sólo las que atendió, y Editor puede responder
 * cualquiera. Los participantes y el owner sirven para filtrar y atribuir, no para restringir lo que
 * se ve — y con el label a secas eso se leía como si fuera un permiso sobre "lo propio".
 */
const LEVEL_OPTIONS = [
  { value: "no_access", label: NIVEL.no_access },
  { value: "viewer", label: NIVEL_PARA_TODO.viewer },
  { value: "editor", label: NIVEL_PARA_TODO.editor },
];

export interface ChannelPermissionsRef {
  /** Persiste los cambios de permisos (lo llama el Save del detalle). */
  persist: () => Promise<void>;
}

/**
 * Tablas de permisos del canal (Operators / Sellers). Cruza la **población** del store (main API)
 * con los **grants** actuales del conversation-service. Sparse: ausencia ⇒ No. El padre dispara
 * `persist()` desde su botón Save; sólo manda las filas que cambiaron.
 */
/** El ancho de los selects de permisos del admin (ver `stores/usersPermissions`): igual en las dos tablas. */
const ANCHO_DEL_SELECT = 180;

const levelLabel = (v: ChannelPermissionLevel) => LEVEL_OPTIONS.find((o) => o.value === v)?.label ?? "No";

export const ChannelPermissions = forwardRef<
  ChannelPermissionsRef,
  { channelId: string; storeCode: string; editable?: boolean; isNew?: boolean }
>(({ channelId, storeCode, editable = true, isNew = false }, ref) => {
    const [operators, setOperators] = useState<StoreOperator[]>([]);
    const [sellers, setSellers] = useState<StoreSeller[]>([]);
    const [opLevels, setOpLevels] = useState<Record<number, ChannelPermissionLevel>>({});
    const [sellerLevels, setSellerLevels] = useState<Record<string, ChannelPermissionLevel>>({});
    const [initialOp, setInitialOp] = useState<Record<number, ChannelPermissionLevel>>({});
    const [initialSeller, setInitialSeller] = useState<Record<string, ChannelPermissionLevel>>({});
    const [loading, setLoading] = useState(true);
    const { data: identity } = useGetIdentity<{ email?: string }>();
    const { token } = theme.useToken();

    useEffect(() => {
      void load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId, storeCode]);

    async function load() {
      setLoading(true);
      // Cargas independientes: un fallo en una fuente (p.ej. sellers 403) no debe vaciar la otra.
      const [opsR, sellsR, opGrantsR, sellerGrantsR] = await Promise.allSettled([
        storePopulationsApi.listOperators(storeCode),
        storePopulationsApi.listSellers(storeCode),
        conversationsApi.getOperatorPermissions(channelId),
        conversationsApi.getSellerPermissions(channelId),
      ]);

      const ops = opsR.status === "fulfilled" ? opsR.value : [];
      const sells = sellsR.status === "fulfilled" ? sellsR.value : [];
      const opGrants = opGrantsR.status === "fulfilled" ? opGrantsR.value : [];
      const sellerGrants = sellerGrantsR.status === "fulfilled" ? sellerGrantsR.value : [];

      setOperators(ops);
      setSellers(sells);

      const opMap: Record<number, ChannelPermissionLevel> = {};
      ops.forEach((o) => (opMap[o.userId] = "no_access"));
      opGrants.forEach((g) => (opMap[g.userId] = g.permissionLevel));
      // Al crear un canal nuevo, el usuario logueado arranca como editor.
      if (isNew && identity?.email) {
        const me = ops.find((o) => o.email === identity.email);
        if (me) opMap[me.userId] = "editor";
      }
      setOpLevels(opMap);
      setInitialOp({ ...opMap });

      const sMap: Record<string, ChannelPermissionLevel> = {};
      sells.forEach((s) => (sMap[s.sellerCode] = "no_access"));
      sellerGrants.forEach((g) => (sMap[g.sellerCode] = g.permissionLevel));
      setSellerLevels(sMap);
      setInitialSeller({ ...sMap });

      if (opsR.status === "rejected" && sellsR.status === "rejected") {
        message.error("No se pudieron cargar las poblaciones de permisos.");
      } else if (sellsR.status === "rejected") {
        message.warning("No se pudieron cargar los sellers (¿falta permiso store_linked_seller?).");
      }
      setLoading(false);
    }

    useImperativeHandle(ref, () => ({
      persist: async () => {
        const opEntries = operators
          .filter((o) => opLevels[o.userId] !== initialOp[o.userId])
          .map((o) => ({ userId: o.userId, level: opLevels[o.userId] }));
        const sellerEntries = sellers
          .filter((s) => sellerLevels[s.sellerCode] !== initialSeller[s.sellerCode])
          .map((s) => ({ sellerCode: s.sellerCode, level: sellerLevels[s.sellerCode] }));

        if (opEntries.length) await conversationsApi.setOperatorPermissions(channelId, opEntries);
        if (sellerEntries.length) await conversationsApi.setSellerPermissions(channelId, sellerEntries);
        setInitialOp({ ...opLevels });
        setInitialSeller({ ...sellerLevels });
      },
    }));

    const opColumns: TableColumnsType<StoreOperator> = [
      {
        // Bug 13 (#109): la columna muestra solo el nombre (sin el email debajo).
        title: "Permisos de operadores",
        dataIndex: "name",
        key: "name",
        onHeaderCell: () => ({ style: { height: 38, padding: "0 16px" } }),
        render: (name: string, r: StoreOperator) => (
          <Flex align="center" gap={8}>
            <span>{name}</span>
            {r.email && identity?.email === r.email && (
              <Text style={{ color: token.colorPrimary }}>Tú</Text>
            )}
          </Flex>
        ),
      },
      {
        title: "",
        key: "perm",
        width: ANCHO_DEL_SELECT + 32,
        render: (_, r) =>
          editable ? (
            <Select
              style={{ width: ANCHO_DEL_SELECT }}
              value={opLevels[r.userId] ?? "no_access"}
              options={LEVEL_OPTIONS}
              onChange={(v) => setOpLevels((p) => ({ ...p, [r.userId]: v as ChannelPermissionLevel }))}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>{levelLabel(opLevels[r.userId] ?? "no_access")}</Text>
          ),
      },
    ];

    const sellerColumns: TableColumnsType<StoreSeller> = [
      { title: "Permisos de sellers", dataIndex: "name", key: "name", onHeaderCell: () => ({ style: { height: 38, padding: "0 16px" } }) },
      {
        title: "",
        key: "perm",
        width: ANCHO_DEL_SELECT + 32,
        render: (_, r) =>
          editable ? (
            <Select
              style={{ width: ANCHO_DEL_SELECT }}
              value={sellerLevels[r.sellerCode] ?? "no_access"}
              options={LEVEL_OPTIONS}
              onChange={(v) =>
                setSellerLevels((p) => ({ ...p, [r.sellerCode]: v as ChannelPermissionLevel }))
              }
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>{levelLabel(sellerLevels[r.sellerCode] ?? "no_access")}</Text>
          ),
      },
    ];

    const tableStyle: React.CSSProperties = {
      border: "1px solid #d9d9d9",
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 16,
      backgroundColor: "#fff",
    };

    return (
      <>
        <div style={tableStyle}>
          <Table<StoreOperator>
            className="tabla-en-contenedor"
            rowKey="userId"
            size="small"
            loading={loading}
            columns={opColumns}
            dataSource={operators}
            pagination={{ pageSize: 5, showTotal: (t) => `${t} operadores` }}
          />
        </div>

        <div style={tableStyle}>
          <Table<StoreSeller>
            className="tabla-en-contenedor"
            rowKey="sellerCode"
            size="small"
            loading={loading}
            columns={sellerColumns}
            dataSource={sellers}
            pagination={{ pageSize: 5, showTotal: (t) => `${t} sellers` }}
          />
        </div>
      </>
    );
  },
);

ChannelPermissions.displayName = "ChannelPermissions";
